import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchCountries } from "@/lib/api";
import { Share2, Facebook, Twitter, MessageCircle, Send, Link2, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLang } from "@/context/LanguageContext";
import { trackEvent } from "@/lib/analytics";
import { socialLinks, canNativeShare, nativeShare as share, copyText } from "@/lib/share";

export default function ShareApp() {
  const { t, lang } = useLang();
  const [copied, setCopied] = useState(false);
  const [params] = useSearchParams();
  const { data: countries = [] } = useQuery({ queryKey: ["countries"], queryFn: fetchCountries, staleTime: 600000 });
  const country = countries.find((c) => c.code === params.get("c"));
  const name = country ? country.names[lang] || country.names.en : null;

  // When a country sheet is open, the header button shares THAT country (deep link + its own OG card), not the whole app.
  const url = country ? `${window.location.origin}/?c=${country.code}&lang=${lang}` : `${window.location.origin}/?lang=${lang}`;
  const shareUrl = country ? `${window.location.origin}/api/share/${country.code}?lang=${lang}` : `${window.location.origin}/api/share/app?lang=${lang}`;
  const text = country ? t("share_country_text").replace("{name}", name) : t("share_app_text");
  const links = socialLinks(shareUrl, text);

  const track = (method) => trackEvent("share", { method, content_type: country ? "country" : "app", item_id: country?.code, lang });

  const items = [
    { id: "whatsapp", label: "WhatsApp", Icon: MessageCircle, href: links.whatsapp, cls: "bg-[#25D366]/10 text-[#128C7E] hover:bg-[#25D366]/20" },
    { id: "facebook", label: "Facebook", Icon: Facebook, href: links.facebook, cls: "bg-[#1877F2]/10 text-[#1877F2] hover:bg-[#1877F2]/20" },
    { id: "twitter", label: "X / Twitter", Icon: Twitter, href: links.twitter, cls: "bg-ink/5 text-ink hover:bg-ink/10" },
    { id: "telegram", label: "Telegram", Icon: Send, href: links.telegram, cls: "bg-[#229ED9]/10 text-[#229ED9] hover:bg-[#229ED9]/20" },
  ];

  const copyLink = async () => {
    track("copy_link");
    await copyText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const nativeShare = () => { track("native"); share({ title: name || t("app_title"), text, url: shareUrl }); };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          aria-label={t("share_app")}
          className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-stoneLine bg-card text-ink hover:bg-ochre hover:text-white hover:border-ochre transition-colors"
          data-testid="share-app-trigger"
        >
          <Share2 className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-4 rounded-2xl border-stoneLine bg-sand" data-testid="share-app-popover">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3" data-testid="share-app-heading">{country ? `${t("share")}: ${name}` : t("share_app")}</div>
        <div className="grid grid-cols-2 gap-2">
          {items.map(({ id, label, Icon, href, cls }) => (
            <a
              key={id}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track(id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${cls}`}
              data-testid={`share-app-${id}`}
            >
              <Icon className="w-4 h-4" /> {label}
            </a>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2 rounded-xl border border-stoneLine bg-card px-3 py-2">
          <span className="flex-1 truncate text-xs text-muted-foreground" dir="ltr" data-testid="share-app-url">{url}</span>
          <button
            onClick={copyLink}
            aria-label={copied ? t("copied") : "Copy link"}
            className={`shrink-0 inline-flex items-center gap-1 text-xs font-medium ${copied ? "text-oasis" : "text-ochre hover:text-ink"}`}
            data-testid="share-app-copy"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
            {copied && t("copied")}
          </button>
        </div>
        {canNativeShare() && (
          <button
            onClick={nativeShare}
            className="mt-2 w-full py-2 rounded-xl bg-ink text-sand text-sm hover:bg-ochre transition-colors"
            data-testid="share-app-native"
          >
            {t("share_app")}…
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
